<script lang="ts" setup>
  import { computed } from 'vue';
  import { useShared } from '@othercommerce/foundation';
  import { useUnitFormatter, Unit } from '@othercommerce/domain';

  interface Price {
    stockUnit: Unit;
    unitPriceDefault: Unit;
    unitPriceBase: string;
    unitPriceFactor: 1 | 10 | 100 | 1000;
    unitPriceNet: string;
    unitPriceGross: string;
    priceNet: string;
    priceGross: string;
    isMedicinalProduct?: boolean;
  }

  interface Props {
    price: Price,
  }

  const props = defineProps<Props>();
  const shared = useShared();
  const unitFormatter = useUnitFormatter();

  const price = computed(() => {
    if (shared.value.includeVat) {
      return props.price.unitPriceGross;
    } else {
      return props.price.unitPriceNet;
    }
  });

  const packageUnit = computed(() => {
    let stockUnit = props.price.stockUnit;

    if (stockUnit !== Unit.Package) {
      return undefined;
    }

    let unit = props.price.unitPriceDefault;
    let base = props.price.unitPriceBase;

    if (unit && base && base !== '1.0000') {
      return unitFormatter.accusative(unit, base);
    }

    return undefined;
  });

  const unit = computed(() => {
    let defaultUnit = props.price.unitPriceDefault;
    let factor = props.price.unitPriceFactor;

    if (defaultUnit && factor) {
      return unitFormatter.accusative(defaultUnit, factor);
    }

    return undefined;
  });

  const visible = computed(() => {
    if (price.value && unit.value) {
      if (shared.value.includeVat) {
        return props.price.unitPriceGross != props.price.priceGross;
      } else {
        return props.price.unitPriceNet != props.price.priceNet;
      }
    }

    return false;
  });
</script>
<template>
  <div v-if="visible && unit && price" class="unit-price">
    <span v-if="packageUnit" class="contains" v-html="$t('SHOP.COMMON.LABEL.UNIT_IN_PACKAGE', { packageUnit })" />
    <span v-html="$t('SHOP.COMMON.LABEL.UNIT_PRICE', { unit })" />
    <SfMoney :value="price" />
  </div>
</template>
<style lang="scss">
  :root {
    --unit-price-color-text: var(--color-default);
    --unit-price-font-family: var(--font-family-sans);
    --unit-price-font-size: var(--font-size-sm);
    --unit-price-font-weight: var(--font-weight-regular);
    --unit-price-padding-y: 0;
    --unit-price-padding-x: 0;
    --unit-price-margin-y: 0;
    --unit-price-margin-x: 0;
  }
</style>
<style lang="scss" scoped>
  .unit-price {
    font-size: var(--unit-price-font-size);
    font-family: var(--unit-price-font-family);
    color: var(--unit-price-color-text);
    font-weight: var(--unit-price-font-weight);
    padding: var(--unit-price-padding-y) var(--unit-price-padding-x);
    margin: var(--unit-price-margin-y) var(--unit-price-margin-x);

    .contains {
      display: block;
    }
  }
</style>
